import * as amplitude from "@amplitude/analytics-browser"

import { userRoleOptions } from "~/models/enum"
import { type UserRole } from "~/providers/userProvider"
import { isAmplitudeEnabled, isProduction } from "~/utils/environment"
import { type User } from "@auth0/auth0-react"

function shouldTrack(): boolean {
  // HAK-2306: Amplitude is fully disabled unless the production SDK key is in use. The guard
  // also stops uninitialized SDK calls from queueing unboundedly (init never runs).
  if (!isAmplitudeEnabled()) return false
  const userId = amplitude.getUserId()
  // Only track events made by a Hakio user in non prod envs.
  return !userId?.includes("hakio") || !isProduction()
}

export function trackEvent(eventName: string, eventProperties?: Record<string, any>) {
  // Great for testing when adding new events.
  if (shouldTrack()) {
    amplitude.track(eventName, eventProperties)
  }
}

export function identifyUserForTracking({
  userId,
  orgId,
  orgName,
  auth0User,
  role,
}: {
  userId?: string
  orgId?: string
  orgName?: string
  auth0User?: User
  role?: UserRole
}) {
  // HAK-2306: skip identity calls entirely when Amplitude isn't loaded.
  if (!isAmplitudeEnabled()) return

  if (userId) {
    amplitude.setUserId(userId)
  }

  if (orgName) {
    amplitude.setGroup("Company", orgName)
  }

  // PRO-1305: the CS dashboards group on a stable org id, so `Company` (keyed by the
  // renameable org name) can't carry them. Kept alongside it — existing charts query it.
  if (orgId) {
    amplitude.setGroup("account_id", orgId)
    const groupProperties = new amplitude.Identify()
    groupProperties.set("account_name", orgName ?? "unknown")
    amplitude.groupIdentify("account_id", orgId, groupProperties)
  }

  const identifyObject = new amplitude.Identify()
  identifyObject.set("department", auth0User?.user_metadata?.department ?? "none")
  if (role) {
    identifyObject.set("role", userRoleOptions.find((r) => r.value === role)?.label ?? "none")
  }
  amplitude.identify(identifyObject)
}
